import { ApplicationRef, Injectable } from '@angular/core';
import { WindowComponent } from 'src/app/components/desktop/window/window.component';
import { WindowStatus } from 'src/app/enums/window-status.enum';
import { ArrayHelper } from 'src/app/helpers/array.helper';
import { AsyncHelper } from 'src/app/helpers/async.helper';
import { MathHelper } from 'src/app/helpers/math.helper';
import { WindowProcess } from 'src/app/interfaces/os/window-process';
import { IWindowProcess } from 'src/app/interfaces/os/window-process.interface';
import { IWindowVisualSettings } from 'src/app/interfaces/ux/window-visual-settings.interface';

@Injectable({
  providedIn: 'root'
})
export class WindowsManagerService {
  private static idCounter = 0;

  visualSettings: IWindowVisualSettings = null;
  windows: IWindowProcess[] = [];
  attachments: any = {};

  constructor(private ref: ApplicationRef) {
    this.loadVisualSettings();
  }

  get openAnimation(): string {
    return ArrayHelper.random(this.visualSettings.animations.open);
  }

  get minimizeAnimation(): string {
    return ArrayHelper.random(this.visualSettings.animations.minimize);
  }

  get maximizeAnimation(): string {
    return ArrayHelper.random(this.visualSettings.animations.maximize);
  }

  get restoreFromMinimizedAnimation(): string {
    return ArrayHelper.random(this.visualSettings.animations.restoreFromMinimized);
  }

  get restoreFromMaximizedAnimation(): string {
    return ArrayHelper.random(this.visualSettings.animations.restoreFromMaximized);
  }

  get closeAnimation(): string {
    return ArrayHelper.random(this.visualSettings.animations.close);
  }

  get openAnimationSafe(): string {
    return this.visualSettings ? this.openAnimation : "";
  }

  get focused(): IWindowProcess {
    return this.windows.find(w => w.extra.z === 100);
  }

  createWindow(title?: string, icon?: string): IWindowProcess {
    const window: IWindowProcess = new WindowProcess({
      id: ++WindowsManagerService.idCounter,
      title: (title || "New window"),
      icon: (icon || null),
    });

    return window;
  }

  isOpen(id: number) {
    return this.windows.find(m => m.id === id);
  }

  setToWindow(id: number, key: string, value: any) {
    if (this.windows.some(m => m.id === id)) {
      this.attachments[id] = this.attachments[id] || {};
      if (value === null || value === undefined) {
        delete this.attachments[id][key];
      }
      else {
        this.attachments[id][key] = value;
      }
    }
  }

  getFromWindow(id: number, key: string): any {
    return (this.attachments && this.attachments[id]) ? this.attachments[id][key] : null;
  }

  addWindow(window: IWindowProcess) {
    if (!window.content) {
      throw new Error("Window content is empty.");
    }

    this.windows.push(window);
    this.focus(window);
    this.refreshGui();
  }

  enableWindow(id: number) {
    /** @todo Complete this method */
  }

  disableWindow(id: number) {
    /** @todo Complete this method */
  }

  maximizeWindow(id: number) {
    const window = this.windows.find(m => m.id === id);
    if (window) {
      this.windows
        .filter(m => m.status === WindowStatus.Maximized)
        .forEach(m => m.status = WindowStatus.Normal);

      window.status = WindowStatus.Maximized;
      window.restore();
      this.focus(window);
      this.refreshGui();
    }
  }

  getScreenSize(): { width: number, height: number } {
    return { width: document.body.clientWidth, height: document.body.clientHeight };
  }

  getWindowPosition(id: number) {
    const component = this.getFromWindow(id, "component") as WindowComponent;
    if (component) {
      return component.getPosition();
    }

    return null;
  }

  setTitle(id: number, title: string) {
    const component = this.getFromWindow(id, "component") as WindowComponent;
    if (component) {
      component.title = title;
    }
  }

  centerWindow(id: number) {
    const component = this.getFromWindow(id, "component") as WindowComponent;
    if (component) {
      const { width, height } = component.getSize();
      const screen = this.getScreenSize();

      const x = screen.width / 2 - width / 2;
      const y = screen.height / 2 - height / 2;

      component.setPosition({ x, y });
    }
  }

  resizeWindow(id: number, width: number, height: number) {
    const component = this.getFromWindow(id, "component") as WindowComponent;
    if (component) {
      component.resize({ width, height });
    }
  }

  restoreWindowStatus(id: number) {
    const window = this.windows.find(m => m.id === id);
    if (window) {
      if (window.isMinimized()) {
        window.restore();
      }
      else {
        window.status = WindowStatus.Normal;
      }
      this.focus(window);
      this.refreshGui();
    }
  }

  minimizeAllWindows() {
    this.windows
      .filter(m => !m.isMinimized())
      .map(m => m.id)
      .forEach(id => this.minimizeWindow(id));
  }

  minimizeWindow(id: number) {
    const window = this.windows.find(m => m.id === id);
    if (window) {
      window.minimize();
      this.focusFirst();
      this.refreshGui();
    }
  }

  disposeWindow(id: number) {
    const i = this.windows.findIndex(m => m.id === id);
    if (i >= 0) {
      this.windows.splice(i, 1);
    }

    delete this.attachments[id];

    this.focusFirst();
    this.refreshGui();
  }

  focus(window) {
    this.bringAllBack();
    window.extra.z = 100;
  }

  focusFirst() {
    const windows = this.windows.filter(m => !m.isMinimized());
    const max = MathHelper.maxItem<IWindowProcess>(windows, (a, b) => a.extra.z - b.extra.z);
    if (max) {
      this.focus(max);
    }
    else {
      this.bringAllBack();
    }
  }

  private bringAllBack() {
    this.windows.forEach(m => m.extra.z -= 1);
  }

  waitForCompleteLoading(): Promise<boolean> {
    return AsyncHelper.waitFor(() => this.visualSettings !== null);
  }

  private refreshGui() {
    this.ref.tick();
  }

  private async loadVisualSettings() {
    const response = await fetch("/assets/os/windows/default-settings.json");
    this.visualSettings = await response.json();
  }
}
